/**
 * T-045 + L1: one Hollywood Trucks entity's certificate holder, additional insured and loss payee.
 * The holder can be filled in from any of the three companies or typed; empty means clients see
 * "contact us" for that entity's bookings. The additional insured and loss payee are the holder
 * unless set to one of the companies or someone else (Eric via Master, 2026-10-29).
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { OTHER_PARTIES, type HolderDraft, type OtherParty } from '@/lib/coiSettingsDraft';
import type { CoiHolder, CoiParty, CoiPartyOption } from '@/lib/portalCoiSettingsApi';

interface Props {
  id: string;
  label: string;
  value: HolderDraft;
  saved: CoiHolder | null;
  suggested: CoiParty | null;
  parties: CoiPartyOption[];
  /** Guidance shown on this card (the Inc.'s: the California LLC is Eric's choice). */
  note?: string;
  /** Shown at the foot of the card (L2: who changed it last, and its short history). */
  footer?: React.ReactNode;
  onChange: (next: HolderDraft) => void;
}

const SAME = 'same';
const CUSTOM = 'custom';
const TITLE: Record<OtherParty, string> = { additional_insured: 'Additional insured', loss_payee: 'Loss payee' };

const same = (a: CoiParty, b: CoiParty | null) =>
  Boolean(b) && a.legal_name.trim() === b!.legal_name.trim() && a.address.trim() === b!.address.trim();

/** Which company a typed party is, if it is one of the three. */
const companyOf = (p: CoiParty, parties: CoiPartyOption[]) => parties.find((o) => same(p, o))?.key ?? null;

function PartyFields({ id, title, party, onChange }: { id: string; title: string; party: CoiParty; onChange: (p: CoiParty) => void }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-name`}>{title} legal name</Label>
        <Input id={`${id}-name`} value={party.legal_name} maxLength={200} onChange={(e) => onChange({ ...party, legal_name: e.target.value })} />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-address`}>{title} address</Label>
        <Textarea id={`${id}-address`} rows={3} value={party.address} maxLength={500} onChange={(e) => onChange({ ...party, address: e.target.value })} />
      </div>
    </div>
  );
}

/** "Same as the certificate holder", one of the companies, or someone else. */
function OtherPartyRow({ id, party, value, parties, onChange }: {
  id: string; party: OtherParty; value: CoiParty | null; parties: CoiPartyOption[]; onChange: (p: CoiParty | null) => void;
}) {
  // "Someone else" stays chosen while its fields are still empty or match no company.
  const [custom, setCustom] = useState(() => Boolean(value) && !companyOf(value!, parties));
  const choice = !value ? SAME : custom ? CUSTOM : companyOf(value, parties) ?? CUSTOM;
  const pick = (next: string) => {
    if (!next) return;
    setCustom(next === CUSTOM);
    if (next === SAME) onChange(null);
    else if (next === CUSTOM) onChange({ legal_name: '', address: '' });
    else {
      const company = parties.find((o) => o.key === next);
      if (company) onChange({ legal_name: company.legal_name, address: company.address });
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1.5 sm:max-w-sm">
        <Label htmlFor={`${id}-choice`} className="text-xs text-muted-foreground">{TITLE[party]}</Label>
        <Select value={choice} onValueChange={pick}>
          <SelectTrigger id={`${id}-choice`} aria-label={TITLE[party]}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={SAME}>Same as the certificate holder</SelectItem>
            {parties.map((o) => <SelectItem key={o.key} value={o.key}>{o.legal_name}</SelectItem>)}
            <SelectItem value={CUSTOM}>Someone else</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {value && <PartyFields id={id} title={TITLE[party]} party={value} onChange={onChange} />}
    </div>
  );
}

export function CoiHolderFields({ id, label, value, saved, suggested, parties, note, footer, onChange }: Props) {
  const empty = !value.legal_name.trim() && !value.address.trim();
  const hasHolder = Boolean(value.legal_name.trim() && value.address.trim());
  const fillIn = (key: string) => {
    const company = parties.find((o) => o.key === key);
    if (company) onChange({ ...value, legal_name: company.legal_name, address: company.address });
  };
  return (
    <section aria-labelledby={`${id}-title`} className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 id={`${id}-title`} className="text-sm font-semibold text-foreground">{label}</h4>
          <p className="text-xs text-muted-foreground">
            {saved ? 'Saved. Clients see this on their certificate requirements.' : 'Not set. Clients see “Contact Hollywood Trucks” for these bookings.'}
          </p>
        </div>
        <div className="flex gap-2">
          {suggested && !same(value, suggested) && (
            <Button type="button" variant="outline" size="sm" aria-label={`Use the approved values: ${label}`} onClick={() => onChange({ ...value, ...suggested })}>Use the approved values</Button>
          )}
          {!empty && (
            <Button type="button" variant="ghost" size="sm" aria-label={`Clear: ${label}`} onClick={() => onChange({ legal_name: '', address: '', additional_insured: null, loss_payee: null })}>Clear</Button>
          )}
        </div>
      </div>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
      {!suggested && (
        <p className="text-xs text-muted-foreground">No approved values yet: Hollywood Trucks hasn’t confirmed who the certificate holder is for these bookings.</p>
      )}

      <div className="flex flex-col gap-1.5 sm:max-w-sm">
        <Label htmlFor={`${id}-fill`} className="text-xs text-muted-foreground">Certificate holder</Label>
        <Select value={companyOf(value, parties) ?? ''} onValueChange={fillIn}>
          <SelectTrigger id={`${id}-fill`} aria-label="Certificate holder: fill in with"><SelectValue placeholder="Fill in with a Hollywood Trucks company…" /></SelectTrigger>
          <SelectContent>
            {parties.map((o) => <SelectItem key={o.key} value={o.key}>{o.legal_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-name`}>Legal name</Label>
          <Input id={`${id}-name`} value={value.legal_name} maxLength={200} onChange={(e) => onChange({ ...value, legal_name: e.target.value })} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-address`}>Address</Label>
          <Textarea id={`${id}-address`} rows={3} value={value.address} maxLength={500} onChange={(e) => onChange({ ...value, address: e.target.value })} />
        </div>
      </div>

      {hasHolder && (
        <div className="flex flex-col gap-3 border-t border-border pt-3">
          {OTHER_PARTIES.map((party) => (
            <OtherPartyRow
              key={party}
              id={`${id}-${party}`}
              party={party}
              value={value[party]}
              parties={parties}
              onChange={(p) => onChange({ ...value, [party]: p })}
            />
          ))}
        </div>
      )}
      {footer}
    </section>
  );
}
